import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Grid from "@mui/material/Grid2";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import Stack from "@mui/material/Stack";
import * as React from "react";
import { useSearchParams } from "react-router-dom";
import { SearchResponseModel } from "../../../models/SearchModels";
import { PageContext } from "../../../context/PageContext";
import { useDebouncedValue } from "../../../hooks/useDebounceValue";
import { useSearchReceivables } from "../../../hooks/useSearchReceivables";
import { SearchInvoiceSelect } from "./SearchInvoiceSelect";
import { SearchFooter } from "./SearchFooter";
import { useTaskpaneTelemetry } from "../../../telemetry/useTaskpaneTelemetry";

const SearchChasables = () => {
  const { trackReceivableSearchQuerySubmitted, trackReceivableSearchResultSelected } = useTaskpaneTelemetry();
  const lastQueryTracked = React.useRef("");
  const [searchParams, setSearchParams] = useSearchParams();
  const inputParam = searchParams.get("input") ?? "";
  const [inputValue, setInputValue] = React.useState(() => inputParam);
  const [debouncedInputValue, debouncedSetInputValue] = useDebouncedValue(inputValue, 400);
  const { result, isLoading } = useSearchReceivables(debouncedInputValue);
  const [value, setValue] = React.useState<SearchResponseModel | null | undefined>(null);
  const { setNewTitle } = React.useContext(PageContext);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = React.useState<string[]>([]);
  const showBackButton = searchParams.get("showBackButton") === "true";

  React.useEffect(() => {
    setNewTitle("Search Receivables");
  }, [setNewTitle]);

  React.useEffect(() => {
    const q = debouncedInputValue.trim();
    if (!q) {
      lastQueryTracked.current = "";
      return;
    }
    if (isLoading) return;
    if (lastQueryTracked.current === q) return;
    lastQueryTracked.current = q;
    trackReceivableSearchQuerySubmitted({
      query_length: String(q.length),
      result_count: String(result?.length ?? 0),
    });
  }, [debouncedInputValue, isLoading, result, trackReceivableSearchQuerySubmitted]);

  React.useEffect(() => {
    setInputValue(inputParam);
    debouncedSetInputValue(inputParam);
  }, [inputParam, debouncedSetInputValue]);

  const valueParam = searchParams.get("value");
  React.useEffect(() => {
    if (!valueParam || !result) {
      if (!valueParam) {
        setValue(null);
      }
      return;
    }

    const found = result.find(({ id }) => valueParam === id) ?? null;
    setValue(found);
  }, [valueParam, result]);

  return (
    <>
      <Stack spacing={2} pb={16} minHeight="100%">
        <Grid size={12} position="sticky" top={0} sx={{ backgroundColor: "white", zIndex: 1 }}>
          <Autocomplete
            id="search"
            fullWidth={true}
            loading={isLoading}
            getOptionLabel={(option) => option.name}
            getOptionKey={(option) => option.id}
            options={result ?? []}
            value={value}
            onChange={(_, newValue) => {
              setValue(newValue);
              setSearchParams(newValue ? { input: newValue.name, value: newValue.id } : undefined);
              if (newValue) {
                trackReceivableSearchResultSelected({ result_type: newValue.type });
              }
            }}
            inputValue={inputValue}
            onInputChange={(_, newInputValue) => {
              setInputValue(newInputValue);
              debouncedSetInputValue(newInputValue);
              if (!newInputValue) {
                setSelectedInvoiceIds([]);
              }
            }}
            noOptionsText={inputValue.length === 0 ? "No result, start typing to search" : "No results"}
            renderInput={(params) => (
              <TextField {...params} label="Search customer or invoice" fullWidth variant="filled" autoFocus={true} />
            )}
            renderOption={(props, option) => {
              const { key: _key, ...optionProps } = props;
              const matches = match(option.name, debouncedInputValue, { insideWords: true, findAllOccurrences: true });

              const parts = parse(option.name, matches);

              return (
                <li key={option.id} {...optionProps}>
                  <Grid container sx={{ alignItems: "center", width: "100%" }}>
                    <Grid sx={{ width: "calc(100% - 44px)", wordWrap: "break-word" }}>
                      {parts.map((part, index) => (
                        <Box key={index} component="span" sx={{ fontWeight: part.highlight ? "bold" : "regular" }}>
                          {part.text}
                        </Box>
                      ))}
                    </Grid>
                  </Grid>
                </li>
              );
            }}
          />
        </Grid>
        {value && (
          <SearchInvoiceSelect
            key={value.id}
            value={value}
            selectedInvoiceIds={selectedInvoiceIds}
            setSelectedInvoiceIds={setSelectedInvoiceIds}
            fullHeight={true}
          />
        )}
      </Stack>
      {value && <SearchFooter selectedInvoiceIds={selectedInvoiceIds} value={value} showBackButton={showBackButton} />}
    </>
  );
};

export default SearchChasables;
